import React from "react";
import { Link } from "react-router-dom";
import { Zap, FileSpreadsheet, Smartphone, Lock, Download, Edit3, ArrowRight, Check, BookOpen, Share2, ListChecks, Layers } from "lucide-react";
import SeoHelmet, { softwareAppSchema, faqSchema } from "../components/SeoHelmet";
import Faq from "../components/Faq";
import RELATED from "../data/related";
import { faqsByTopic } from "../data/faqs";
import { useAuth } from "../context/AuthContext";

const features = [
    { icon: Zap, title: "Lightning fast", body: "Opens 100k-row CSVs in milliseconds. No spinners. No bloat. Pure speed." },
    { icon: FileSpreadsheet, title: "Edit & save", body: "Click any cell, edit inline, add/remove rows & columns, save back as clean CSV." },
    { icon: Smartphone, title: "Any device", body: "Phone, tablet, laptop — works in any browser. No install required." },
    { icon: Download, title: "Download anywhere", body: "Export your edits as a fresh CSV in one click. Your data stays your data." },
    { icon: Lock, title: "Private by default", body: "Your files load locally in your browser. Optional cloud save when you want it." },
    { icon: Edit3, title: "Free to view", body: "Open any CSV instantly — no signup, no card, no payment. Pay $9.99/mo only if you want to edit & save." },
];

export default function Landing() {
    const homeFaqs = faqsByTopic("general", "open", "edit", "lightweight", "privacy", "pricing");
    const { user } = useAuth();
    const isPaid = !!user && (user.is_paid || user.role === "admin");
    return (
        <>
            <SeoHelmet
                title="OpenCSV — The Fastest CSV Viewer & Editor"
                description="Open, edit, save, and share CSV files instantly on any device. Free to view, $9.99/mo to edit anywhere. No installs, no tracking."
                schema={[softwareAppSchema, faqSchema(homeFaqs)]}
            />

            {/* Hero */}
            <section className="relative border-b border-black/10 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage: "url(https://images.unsplash.com/photo-1729258882260-1c33b6c5b183?crop=entropy&cs=srgb&fm=jpg&q=85)",
                        backgroundSize: "cover", backgroundPosition: "center",
                    }}
                />
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-20 md:py-28 grid md:grid-cols-12 gap-10 relative">
                    <div className="md:col-span-7 animate-fade-up">
                        <div className="inline-flex items-center gap-2 border border-ink/15 px-3 py-1 ui-heading text-[11px] uppercase tracking-widest text-ink-soft mb-6" data-testid="hero-badge">
                            <span className="w-1.5 h-1.5 bg-signal-green inline-block" /> v1 — live now
                        </div>
                        <h1 className="font-display font-black tracking-tighter text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
                            <span className="font-square text-ink">CSV</span><span className="text-ink">.</span><br />
                            <span className="text-cobalt">Done right.</span>
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-ink-soft max-w-xl">
                            Open any CSV — free, in your browser, on any device. Edit, save, sync &amp; AI-clean it for $9.99/mo. The opposite of bloated spreadsheets.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            {(!user || (!user.is_paid && user.role !== "admin")) ? (
                                <>
                                    <Link to="/app" className="btn-brutal inline-flex items-center justify-center gap-2 bg-cobalt hover:bg-cobalt-hover text-white px-6 py-3 ui-heading font-semibold border border-cobalt" data-testid="hero-open-viewer-cta">
                                        Open the free viewer <ArrowRight size={16} />
                                    </Link>
                                    <Link to="/pricing" className="btn-brutal inline-flex items-center justify-center gap-2 bg-white text-ink px-6 py-3 ui-heading font-semibold border border-ink" data-testid="hero-pricing-cta">
                                        See Pro plans
                                    </Link>
                                </>
                            ) : (
                                <Link to="/app" className="btn-brutal inline-flex items-center justify-center gap-2 bg-cobalt hover:bg-cobalt-hover text-white px-6 py-3 ui-heading font-semibold border border-cobalt" data-testid="hero-open-editor-cta">
                                    Open the editor <ArrowRight size={16} />
                                </Link>
                            )}
                        </div>
                        <ul className="mt-8 grid grid-cols-2 gap-2 max-w-md text-sm text-ink-soft">
                            {["Free to view", "No watermark", "No file size tax", "Works offline"].map((t) => (
                                <li key={t} className="flex items-center gap-2"><Check size={14} className="text-cobalt" /> {t}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-5 relative">
                        <div className="border border-ink/15 bg-white shadow-sharp">
                            <div className="flex items-center justify-between border-b border-ink/10 px-4 py-2 bg-paper">
                                <div className="flex gap-1.5">
                                    <span className="w-2.5 h-2.5 bg-ink/15 rounded-full" />
                                    <span className="w-2.5 h-2.5 bg-ink/15 rounded-full" />
                                    <span className="w-2.5 h-2.5 bg-ink/15 rounded-full" />
                                </div>
                                <span className="font-mono text-[11px] text-ink-muted">customers.csv</span>
                            </div>
                            <table className="csv-grid w-full">
                                <thead>
                                    <tr>
                                        <th className="row-num"> </th>
                                        <th>name</th>
                                        <th>email</th>
                                        <th>plan</th>
                                        <th>mrr</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        ["Aria Chen", "aria@studio.io", "pro", "29.00"],
                                        ["Niko Park", "niko@labs.dev", "team", "99.00"],
                                        ["Sam Webb", "sam@maker.cc", "pro", "29.00"],
                                        ["Lex Rios", "lex@solo.app", "free", "0.00"],
                                        ["Mira Kahn", "mira@pixel.io", "team", "99.00"],
                                        ["Dee Olu", "dee@ship.fm", "pro", "29.00"],
                                    ].map((r, i) => (
                                        <tr key={i}>
                                            <td className="row-num">{i + 1}</td>
                                            {r.map((c, j) => <td key={j} className="px-2 py-1.5">{c}</td>)}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="border-t border-ink/10 px-3 py-2 flex items-center justify-between text-[11px] font-mono text-ink-muted">
                                <span>6 rows · 4 cols</span>
                                <span>UTF-8 · auto-detected</span>
                            </div>
                        </div>
                        <div className="absolute -top-3 -right-3 hidden md:block bg-ink text-white px-3 py-1.5 ui-heading text-xs">
                            instant load
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="border-b border-black/10">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-20">
                    <div className="grid md:grid-cols-12 gap-6 mb-12">
                        <div className="md:col-span-5">
                            <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ what's inside</div>
                            <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
                                Everything you need.<br />Nothing you don't.
                            </h2>
                        </div>
                        <p className="md:col-span-6 md:col-start-7 text-ink-soft text-base self-end">
                            We obsessed over one thing: making CSVs feel as fast and friendly as a notepad. No bloated grid libraries, no sluggish spreadsheets. Just your data, instantly.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                        {features.map(({ icon: Icon, title, body }) => (
                            <div key={title} className="bg-white p-8 hover:bg-paper transition-colors duration-150" data-testid={`feature-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                                <Icon size={20} className="text-cobalt mb-4" />
                                <h3 className="ui-heading font-bold text-base mb-2">{title}</h3>
                                <p className="text-sm text-ink-soft leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Topic hubs (SEO landing links) */}
            <section className="border-b border-black/10 bg-paper" data-testid="topic-hubs">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-20">
                    <div className="grid md:grid-cols-12 gap-6 mb-10">
                        <div className="md:col-span-5">
                            <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ explore</div>
                            <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">Everything CSV. <span className="text-cobalt">In one place.</span></h2>
                        </div>
                        <p className="md:col-span-6 md:col-start-7 text-ink-soft text-base self-end">
                            We wrote plain-English guides to every CSV question we kept getting asked — what they are, how to open them, how to edit them, when to use them instead of Excel. Browse the topics or skip straight to the editor.
                        </p>
                    </div>
                    <ul className="grid md:grid-cols-3 gap-4">
                        {[
                            { ...RELATED.viewer, icon: FileSpreadsheet },
                            { ...RELATED.editor, icon: Edit3 },
                            { ...RELATED.what, icon: BookOpen },
                            { ...RELATED.edit, icon: ListChecks },
                            { ...RELATED.vs, icon: Layers },
                            { ...RELATED.use, icon: Share2 },
                            { ...RELATED.light, icon: Zap },
                            { ...RELATED.privacy, icon: Lock },
                            { ...RELATED.fast, icon: Smartphone },
                        ].map((r) => {
                            const Icon = r.icon;
                            return (
                                <li key={r.href}>
                                    <Link to={r.href} className="block border border-black/10 bg-white p-6 hover:border-cobalt transition-colors h-full" data-testid={`hub-${r.href.replace(/\//g, '')}`}>
                                        <Icon size={18} className="text-cobalt mb-3" />
                                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">{r.eyebrow}</div>
                                        <div className="font-display font-bold text-xl mt-1">{r.label}</div>
                                        <p className="text-sm text-ink-soft mt-2">{r.summary}</p>
                                        <span className="mt-4 inline-flex items-center gap-1 text-sm text-cobalt ui-heading font-semibold">Read <ArrowRight size={12} /></span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </section>

            {/* Use cases band */}
            <section className="border-b border-black/10" data-testid="use-cases-band">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-20">
                    <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ who uses opencsv</div>
                    <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-10">Built for everyone CSV touches.</h2>
                    <div className="grid md:grid-cols-2 gap-8">
                        {[
                            ["Marketers", "Clean email lists, fix audience exports, deduplicate before launching campaigns."],
                            ["Developers", "Preview API exports and database dumps before piping data into the next system."],
                            ["Ecommerce sellers", "Bulk-update prices and stock for Shopify, WooCommerce, Amazon, Etsy."],
                            ["Operations & finance", "Edit accounting exports, fix vendor names, recategorise expenses fast."],
                            ["Students & researchers", "Open public datasets and tweak survey results without firing up SPSS."],
                            ["AI & data teams", "Inspect training data and fine-tuning sets without breaking encoding."],
                        ].map(([t, b]) => (
                            <div key={t} className="border border-black/10 bg-white p-6">
                                <h3 className="font-display font-bold text-xl">{t}</h3>
                                <p className="text-sm text-ink-soft mt-2">{b}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-8">
                        <Link to="/csv-use-cases" className="ui-heading text-sm text-cobalt hover:underline inline-flex items-center gap-1">See full list of use cases <ArrowRight size={12} /></Link>
                    </div>
                </div>
            </section>

            {/* Trust band */}
            <section className="border-b border-black/10 bg-ink text-white" data-testid="trust-band">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-16 grid md:grid-cols-3 gap-10">
                    <div>
                        <Lock className="text-signal-green mb-3" />
                        <h3 className="font-display font-bold text-xl">Privacy-first</h3>
                        <p className="text-sm text-white/70 mt-2">Your CSV files load and edit locally in your browser. We never read your data, never sell it, never train on it.</p>
                        <Link to="/privacy-focused-csv-viewer" className="mt-3 inline-block text-sm text-signal-green ui-heading hover:underline">How privacy works →</Link>
                    </div>
                    <div>
                        <Zap className="text-signal-green mb-3" />
                        <h3 className="font-display font-bold text-xl">Lightning fast</h3>
                        <p className="text-sm text-white/70 mt-2">A streaming parser and virtualised table render 100,000 rows in under a second on a phone.</p>
                        <Link to="/fast-csv-viewer" className="mt-3 inline-block text-sm text-signal-green ui-heading hover:underline">See the benchmarks →</Link>
                    </div>
                    <div>
                        <Smartphone className="text-signal-green mb-3" />
                        <h3 className="font-display font-bold text-xl">Any device</h3>
                        <p className="text-sm text-white/70 mt-2">Works in any modern browser. Native iOS and Android apps coming soon. Your account works everywhere.</p>
                        {!isPaid && <Link to="/pricing" className="mt-3 inline-block text-sm text-signal-green ui-heading hover:underline">See Pro plans →</Link>}
                    </div>
                </div>
            </section>

            {/* Latest from the blog */}
            <section className="border-b border-black/10" data-testid="home-blog-band">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-20">
                    <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                        <div>
                            <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ from the blog</div>
                            <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">Plain-English guides.</h2>
                        </div>
                        <Link to="/blog" className="ui-heading text-sm text-cobalt hover:underline inline-flex items-center gap-1">All articles <ArrowRight size={12} /></Link>
                    </div>
                    <ul className="grid md:grid-cols-3 gap-4">
                        {[
                            { slug: "how-csv-files-work", title: "How CSV Files Work", excerpt: "Open one in Notepad and we'll decode it line by line." },
                            { slug: "best-csv-editor", title: "What Makes a Good CSV Editor in 2026?", excerpt: "Five things that actually matter — speed, privacy, mobile, and more." },
                            { slug: "why-csv-still-matters", title: "Why CSV Still Matters in 2026", excerpt: "It's older than the web. AI made it more important than ever." },
                        ].map((p) => (
                            <li key={p.slug}>
                                <Link to={`/blog/${p.slug}`} className="block border border-black/10 bg-white p-6 hover:border-cobalt transition-colors h-full">
                                    <div className="ui-heading text-xs uppercase tracking-widest text-cobalt">/ article</div>
                                    <div className="font-display font-bold text-xl mt-2">{p.title}</div>
                                    <p className="text-sm text-ink-soft mt-2">{p.excerpt}</p>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* CTA — only shown to non-paid users */}
            {!isPaid && (
                <section className="border-b border-black/10">
                    <div className="mx-auto max-w-7xl px-4 md:px-8 py-24 grid md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-7">
                            <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95]">
                                Free to view.<br />$9.99 to edit.
                            </h2>
                            <p className="mt-5 text-ink-soft max-w-lg">
                                Anyone can open and view CSVs in OpenCSV — that's free, no card. Edit, save, share with edit permissions, and run the export presets with Pro. Start with a 14-day free trial, no card up front. Cancel anytime, 30-day money-back.
                            </p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <Link to="/app" className="btn-brutal inline-flex items-center gap-2 bg-cobalt text-white px-6 py-3 ui-heading font-semibold border border-cobalt" data-testid="cta-open-viewer">
                                    Open free viewer <ArrowRight size={16} />
                                </Link>
                                <Link to="/pricing" className="btn-brutal inline-flex items-center gap-2 bg-ink text-white px-6 py-3 ui-heading font-semibold border border-ink" data-testid="cta-see-pro">
                                    See Pro plans
                                </Link>
                            </div>
                        </div>
                        <div className="md:col-span-5 border border-ink/15 bg-white p-8 shadow-sharp" data-testid="pricing-card-home">
                            <div className="flex items-baseline gap-2">
                                <span className="font-display font-black text-6xl tracking-tighter">$9.99</span>
                                <span className="text-ink-soft text-sm">/ month USD</span>
                            </div>
                            <div className="ui-heading text-xs uppercase tracking-widest text-cobalt mt-2">Pro · or $79/year (save 34%)</div>
                            <ul className="mt-5 space-y-2 text-sm">
                                {["Edit unlimited CSVs", "Save to cloud across devices", "All 9 export presets", "Share with edit permissions", "AI features (coming soon)", "Cancel anytime · 30-day refund"].map((t) => (
                                    <li key={t} className="flex items-center gap-2"><Check size={14} className="text-cobalt" /> {t}</li>
                                ))}
                            </ul>
                            <Link to="/pricing" className="btn-brutal mt-5 w-full inline-flex items-center justify-center gap-2 bg-cobalt text-white px-4 py-2.5 ui-heading text-sm font-semibold border border-cobalt" data-testid="cta-card-trial">
                                Start 14-day free trial <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* "Welcome back" CTA for paid users */}
            {isPaid && (
                <section className="border-b border-black/10 bg-paper">
                    <div className="mx-auto max-w-7xl px-4 md:px-8 py-20 text-center">
                        <div className="ui-heading text-xs uppercase tracking-widest text-cobalt">/ welcome back</div>
                        <h2 className="mt-2 font-display font-black tracking-tighter text-4xl md:text-5xl">You're all set.</h2>
                        <p className="mt-4 text-ink-soft max-w-md mx-auto">Your Pro access is active. Open the editor and get to work.</p>
                        <Link to="/app" className="btn-brutal mt-7 inline-flex items-center gap-2 bg-cobalt text-white px-6 py-3 ui-heading font-semibold border border-cobalt" data-testid="cta-open-editor">
                            Open the editor <ArrowRight size={16} />
                        </Link>
                    </div>
                </section>
            )}

            {/* FAQ at bottom of homepage */}
            {(() => {
                const Faq = require("../components/Faq").default;
                return <Faq items={homeFaqs} title="Common questions about CSV & OpenCSV" testIdPrefix="home-faq" />;
            })()}
        </>
    );
}
